import {
  ClerkProvider,
  useClerk,
  useSession as useClerkSession,
  useUser,
} from "@clerk/clerk-react";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";

import type { AuthMode, ClientAuthConfig, Session, User } from "./auth-types";
import { IframeAuthClient } from "./iframe-auth";

export interface HybridAuthContextValue {
  session: Session | null;
  isLoading: boolean;
  error: string | null;
  mode: AuthMode;
  getToken: () => Promise<string | null>;
  signOut: () => Promise<void>;
  signIn: () => Promise<void>;
}

const HybridAuthContext = createContext<HybridAuthContextValue | null>(null);

/**
 * Props for HybridAuthProvider
 */
interface HybridAuthProviderProps {
  children: ReactNode;
  fallback?: ReactNode;
  config: ClientAuthConfig;
}

/**
 * Satellite mode auth state provider that uses Clerk hooks
 */
function SatelliteAuthStateProvider({ children }: { children: ReactNode }) {
  const clerk = useClerk();
  const { user: clerkUser } = useUser();
  const { session: clerkSession, isLoaded: sessionLoaded } = useClerkSession();

  // State management
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Update session for satellite mode based on Clerk state
   */
  const updateSatelliteSession = useCallback(async () => {
    if (!sessionLoaded) {
      setIsLoading(true);
      return;
    }

    let authUser: User | null = null;
    if (clerkUser) {
      authUser = {
        id: clerkUser.id,
        email: clerkUser.primaryEmailAddress?.emailAddress || null,
        name:
          clerkUser.fullName ||
          `${clerkUser.firstName || ""} ${clerkUser.lastName || ""}`.trim() ||
          null,
        role: (clerkUser.publicMetadata?.role as string) || null,
      };
    }

    setSession({
      token: (await clerkSession?.getToken()) ?? undefined,
      authenticated: !!clerkSession && !!clerkUser,
      user: authUser,
      mode: "satellite",
    });
    setIsLoading(false);
    setError(null);
  }, [clerkSession, clerkUser, sessionLoaded]);

  useEffect(() => {
    updateSatelliteSession();
  }, [clerkUser, clerkSession, sessionLoaded, updateSatelliteSession]);

  const getToken = async (): Promise<string | null> => {
    try {
      return clerkSession ? await clerkSession.getToken() : null;
    } catch (err) {
      console.error("Failed to get token:", err);
      return null;
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      await clerk.signOut();
    } catch (err) {
      console.error("Failed to sign out:", err);
      throw err;
    }
  };

  const signIn = async (): Promise<void> => {
    clerk.redirectToSignIn();
  };

  const contextValue: HybridAuthContextValue = {
    session,
    isLoading,
    error,
    mode: "satellite",
    getToken,
    signOut,
    signIn,
  };

  return <HybridAuthContext.Provider value={contextValue}>{children}</HybridAuthContext.Provider>;
}

/**
 * Iframe mode auth state provider that doesn't use Clerk hooks
 */
function IframeAuthStateProvider({ children }: { children: ReactNode }) {
  // State management
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [iframeAuthClient] = useState(() => new IframeAuthClient());

  /**
   * Fetch session for iframe mode
   */
  const fetchIframeSession = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const authSession = await iframeAuthClient.getSession();
      setSession({
        ...authSession,
        mode: "iframe",
      });

      if (authSession.error) {
        setError(authSession.error);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Authentication failed";
      setError(errorMessage);
      setSession({
        authenticated: false,
        user: null,
        error: errorMessage,
        mode: "iframe",
      });
    } finally {
      setIsLoading(false);
    }
  }, [iframeAuthClient]);

  useEffect(() => {
    fetchIframeSession();
  }, [fetchIframeSession]);

  const signOut = async (): Promise<void> => {
    setSession(null);
    setIsLoading(false);
  };

  const contextValue: HybridAuthContextValue = {
    session,
    isLoading,
    error,
    mode: "iframe",
    getToken: iframeAuthClient.getToken,
    signIn: fetchIframeSession,
    signOut,
  };

  return <HybridAuthContext.Provider value={contextValue}>{children}</HybridAuthContext.Provider>;
}

/**
 * Main hybrid authentication provider
 *
 * Automatically detects auth mode and provides the appropriate provider:
 * - Iframe mode: Uses existing iframe auth system
 * - Satellite mode: Wraps with ClerkProvider for satellite domain auth
 */
export function HybridAuthProvider({ children, config }: HybridAuthProviderProps) {
  // In satellite mode, wrap with ClerkProvider
  if (config?.authMode === "satellite") {
    if (!config.clerkPublishableKey) {
      throw new Error("Clerk publishable key not configured");
    }

    console.log("HybridAuthProvider config", config);

    return (
      <ClerkProvider
        publishableKey={config.clerkPublishableKey}
        isSatellite={true}
        domain={config.baseUrl}
        signInUrl={`${config.controlPlaneUrl}${config.controlPlaneSignInPath}`}
        signUpUrl={`${config.controlPlaneUrl}${config.controlPlaneSignUpPath}`}
        signInForceRedirectUrl={config.afterSignInPath}
        signUpForceRedirectUrl={config.afterSignUpPath}
      >
        <SatelliteAuthStateProvider>{children}</SatelliteAuthStateProvider>
      </ClerkProvider>
    );
  }

  // In iframe mode, just use our iframe auth state provider
  return <IframeAuthStateProvider>{children}</IframeAuthStateProvider>;
}

/**
 * Hook to access hybrid auth context
 */
export function useAuth() {
  const context = useContext(HybridAuthContext);
  if (!context) {
    throw new Error("useHybridAuth must be used within a HybridAuthProvider");
  }
  return {
    ...context,
    user: context.session?.user,
    isAuthenticated: context.session?.authenticated,
  };
}
